import { fetchAuthSession, signOut } from "aws-amplify/auth";
import type {
  Job,
  Reference,
  UserProfile,
  PaginatedResult,
  Stats,
  SubmitJobRequest,
  VideoJob,
  VideoJobCreate,
} from "./types";

const API_URL = import.meta.env.VITE_API_URL ?? "";

// ── Fetch wrapper ──────────────────────────────────────────────────────

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error("Not authenticated");
  return token;
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getToken();
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    // Auto-logout on 401 (deleted/invalid account) or 403 (suspended)
    if (response.status === 401 || response.status === 403) {
      const body = await response.json().catch(() => ({ error: "Request failed" }));
      const msg = body.error ?? "";
      if (msg.includes("not found") || msg.includes("suspended") || response.status === 401) {
        await signOut().catch(() => {});
        window.location.href = "/login";
        throw new Error(msg || "Session expired");
      }
    }
    const body = await response.json().catch(() => ({ error: "Request failed" }));
    throw new Error(body.error ?? `HTTP ${response.status}`);
  }

  // Handle empty/void responses gracefully
  const text = await response.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined as T;
  }
}

// ── Jobs ───────────────────────────────────────────────────────────────

export async function submitJob(data: SubmitJobRequest): Promise<{ jobId: string }> {
  return request("/jobs", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getJob(jobId: string): Promise<Job> {
  return request(`/jobs/${jobId}`);
}

export async function listJobs(
  limit = 25,
  cursor?: string
): Promise<PaginatedResult<Job>> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set("cursor", cursor);
  return request(`/jobs?${params}`);
}

export async function cancelJob(jobId: string): Promise<void> {
  await request(`/jobs/${jobId}/cancel`, { method: "POST" });
}

export async function deleteJob(jobId: string): Promise<void> {
  await request(`/jobs/${jobId}`, { method: "DELETE" });
}

export async function batchDeleteJobs(jobIds: string[]): Promise<{
  success: string[];
  failed: Array<{ jobId: string; error: string }>;
}> {
  return request("/jobs/batch-delete", {
    method: "POST",
    body: JSON.stringify({ jobIds }),
  });
}

export async function updateJob(
  jobId: string,
  data: { displayName?: string; displayImageUrl?: string }
): Promise<void> {
  await request(`/jobs/${jobId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// ── Video Jobs ─────────────────────────────────────────────────────────

export async function submitVideoJob(
  parentJobId: string,
  data: VideoJobCreate
): Promise<VideoJob> {
  return request(`/jobs/${parentJobId}/videos`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function listVideoJobs(parentJobId: string): Promise<VideoJob[]> {
  return request(`/jobs/${parentJobId}/videos`);
}

export async function getVideoJob(
  parentJobId: string,
  videoJobId: string
): Promise<VideoJob> {
  return request(`/jobs/${parentJobId}/videos/${videoJobId}`);
}

export async function deleteVideoJob(
  parentJobId: string,
  videoJobId: string
): Promise<void> {
  await request(`/jobs/${parentJobId}/videos/${videoJobId}`, { method: "DELETE" });
}

// ── Share ──────────────────────────────────────────────────────────────

export async function shareJob(
  jobId: string,
  takeIndex = 0
): Promise<{ shareUrl: string; shareId: string }> {
  return request(`/jobs/${jobId}/share`, {
    method: "POST",
    body: JSON.stringify({ takeIndex }),
  });
}

// ── Upload ─────────────────────────────────────────────────────────────

export async function getUploadUrl(
  purpose: "reference" | "source" | "reference-image" | "reference-image-original" | "reference-video",
  contentType: string,
  filename: string
): Promise<{ uploadUrl: string; key: string }> {
  return request("/upload-url", {
    method: "POST",
    body: JSON.stringify({ purpose, contentType, filename }),
  });
}

/**
 * Server-side: SSRF-validate URL → download → store in S3 under
 * `reference-images-original/`. Returns the new S3 key plus a presigned GET
 * the cropper can load with `crossOrigin="anonymous"`.
 */
export async function uploadImageFromUrl(
  url: string,
): Promise<{ key: string; previewUrl: string }> {
  return request("/references/image-from-url", {
    method: "POST",
    body: JSON.stringify({ url }),
  });
}

export async function uploadFile(
  uploadUrl: string,
  file: Blob,
  contentType: string
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });
  if (!response.ok) {
    throw new Error("Upload failed");
  }
}

// ── YouTube ──────────────────────────────────────────────────────────────

export async function getYouTubeInfo(url: string): Promise<{
  videoId: string;
  title: string;
  thumbnail: string;
  duration: number;
}> {
  return request("/youtube/info", {
    method: "POST",
    body: JSON.stringify({ url }),
  });
}

export async function extractYouTubeAudio(url: string, videoId: string, title?: string, thumbnail?: string): Promise<{
  taskId: string;
  status: string;
}> {
  return request("/youtube/extract", {
    method: "POST",
    body: JSON.stringify({ url, videoId, title, thumbnail }),
  });
}

export async function getYouTubeStatus(taskId: string): Promise<{
  taskId: string;
  status: string;
  s3Key?: string;
  previewUrl?: string;
  duration?: number;
  contentType?: string;
  error?: string;
  createdAt: number;
  updatedAt: number;
}> {
  return request(`/youtube/status/${taskId}`);
}

// ── References ─────────────────────────────────────────────────────────

export async function createReference(data: {
  name: string;
  s3Key: string;
  duration: number;
  imageUrl?: string;
}): Promise<Reference> {
  return request("/references", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// Lazy backfill for legacy rows that lack pre-computed peaks. The UI fires
// this once per asset after the decode-fallback path succeeds; failures are
// swallowed so backfill never surfaces as a user-visible error.
export type SetAudioPeaksTarget =
  | { kind: "reference-voice-preview"; refName: string }
  | { kind: "take"; jobId: string; takeIndex: number };

export async function setAudioPeaks(
  target: SetAudioPeaksTarget,
  peaks: string,
): Promise<void> {
  await request("/audio/peaks", {
    method: "POST",
    body: JSON.stringify({ ...target, peaks }),
  });
}

/**
 * Save the reference images that preload whenever a video is generated from a
 * job using this voice. Full replacement; an empty array clears them.
 */
export async function setVoiceVideoRefs(
  name: string,
  images: { key: string; label?: string }[],
): Promise<void> {
  await request(`/references/${encodeURIComponent(name)}/video-refs`, {
    method: "PUT",
    body: JSON.stringify({ images }),
  });
}

export async function listReferences(): Promise<{ items: Reference[]; provided?: Reference[] }> {
  return request("/references");
}

export async function deleteReference(name: string): Promise<void> {
  // Use POST with body to avoid API Gateway path encoding issues with slashes
  await request("/references/delete", {
    method: "POST",
    body: JSON.stringify({ name }),
  });
}

/** Double-encode so API Gateway's automatic percent-decoding doesn't
 *  turn special chars (? # / etc.) back into URL delimiters. After APIGW
 *  decodes one level, the value remains safely percent-encoded for the Lambda. */
function encodeRefName(name: string): string {
  return encodeURIComponent(name).replace(/%/g, "%25");
}

export async function updateReference(
  name: string,
  data: {
    s3Key: string;
    duration: number;
    /** S3 key of the cropped derivative, "" to clear, or external URL for the legacy path. */
    imageUrl?: string;
    /** S3 key of the original (untouched) upload, kept for re-cropping. */
    imageOriginalS3Key?: string;
    /** Crop region (0-1 relative) on the original. null clears it. */
    imageCrop?: { x: number; y: number; w: number; h: number } | null;
  }
): Promise<void> {
  await request(`/references/${encodeRefName(name)}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function renameReference(
  name: string,
  newName: string,
  userId?: string
): Promise<void> {
  // POST with body to avoid API Gateway path encoding issues (names can
  // contain %, ?, #, / and similar chars that break path-param routing).
  await request(`/references/rename`, {
    method: "POST",
    body: JSON.stringify({ name, newName, ...(userId ? { userId } : {}) }),
  });
}

/**
 * Manually (re)generate the voice preview for a reference. Owner-only by
 * default; admins can pass `ownerUserId` to retry someone else's preview
 * (used by the admin Voices page). Returns 202 — the actual render runs
 * out of band; UI sees status flip on the next listReferences refresh.
 */
export async function triggerVoicePreview(
  name: string,
  ownerUserId?: string,
): Promise<{ status: string; name: string }> {
  const qs = ownerUserId ? `?ownerUserId=${encodeURIComponent(ownerUserId)}` : "";
  return request(`/references/${encodeURIComponent(name)}/voice-preview${qs}`, {
    method: "POST",
  });
}

// ── Admin ──────────────────────────────────────────────────────────────

export async function listUsers(
  limit = 100,
  cursor?: string
): Promise<PaginatedResult<UserProfile>> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set("cursor", cursor);
  return request(`/admin/users?${params}`);
}

export async function createUser(data: {
  email: string;
  role?: "user" | "super_admin";
  password?: string;
}): Promise<UserProfile> {
  return request("/admin/users", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateUser(
  userId: string,
  data: { role?: "user" | "super_admin"; status?: "active" | "suspended" }
): Promise<UserProfile> {
  return request(`/admin/users/${userId}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function listAllJobs(
  limit = 25,
  cursor?: string
): Promise<PaginatedResult<Job>> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set("cursor", cursor);
  return request(`/admin/jobs?${params}`);
}

export async function listJobsByUser(
  userId: string,
  limit = 25,
  cursor?: string
): Promise<PaginatedResult<Job>> {
  const params = new URLSearchParams({ userId, limit: String(limit) });
  if (cursor) params.set("cursor", cursor);
  return request(`/admin/jobs?${params}`);
}

export async function deleteAnyJob(
  jobId: string,
  userId: string
): Promise<void> {
  await request(`/admin/jobs/${jobId}?userId=${userId}`, {
    method: "DELETE",
  });
}

export async function listAllReferences(): Promise<{
  items: Array<Reference & { userId: string; provided?: boolean }>;
}> {
  return request("/admin/references");
}

/** Cheap summary used by the Library admin section: just owners + counts. */
export async function listAllReferenceOwners(): Promise<{
  owners: Array<{ userId: string; count: number }>;
}> {
  return request("/admin/references?summary=1");
}

/** Per-user refs, fetched lazily when an admin expands a username. */
export async function listReferencesByUser(
  userId: string,
): Promise<{ items: Array<Reference & { userId: string; provided?: boolean }> }> {
  return request(`/admin/references?userId=${encodeURIComponent(userId)}`);
}

export async function getStats(): Promise<Stats> {
  return request("/admin/stats");
}

// ── Admin: Reset Password ──────────────────────────────────────────────

export async function resetUserPassword(
  userId: string,
  password: string
): Promise<void> {
  await request(`/admin/users/${userId}/password`, {
    method: "PUT",
    body: JSON.stringify({ password }),
  });
}

// ── Admin: Delete User ─────────────────────────────────────────────────

export async function deleteUser(userId: string): Promise<void> {
  await request(`/admin/users/${userId}`, { method: "DELETE" });
}

export async function getOrphanedUsers(): Promise<{
  orphans: import("./types").UserProfile[];
  cognitoCount: number;
  dynamoCount: number;
}> {
  return request("/admin/users/orphans");
}

export async function deleteOrphan(userId: string): Promise<void> {
  await request(`/admin/users/${userId}/orphan`, { method: "DELETE" });
}

// ── Admin: Mark Reference Provided ─────────────────────────────────────

export async function markReferenceProvided(
  userId: string,
  name: string,
  provided: boolean
): Promise<void> {
  await request("/admin/references/provided", {
    method: "PUT",
    body: JSON.stringify({ userId, name, provided }),
  });
}

export async function setReferenceDefaults(
  userId: string,
  name: string,
  defaults: import("./types").VoiceDefaults | null
): Promise<void> {
  await request("/admin/references/defaults", {
    method: "PUT",
    body: JSON.stringify({ userId, name, defaults }),
  });
}

/**
 * Admin: queue voice previews for references across all users.
 *   - "missing": refs without a completed preview at any version (default)
 *   - "stale":   refs rendered against a different VOICE_PREVIEW_VERSION
 *   - "all":     every ref, no matter what
 */
export async function backfillVoicePreviews(
  scope: "missing" | "stale" | "all" = "missing",
  maxJobs = 200,
): Promise<{ enqueued: number; skipped: number; total: number; scope: string; maxJobs: number }> {
  return request("/admin/voice-previews/backfill", {
    method: "POST",
    body: JSON.stringify({ scope, maxJobs }),
  });
}

/**
 * Admin: flip every reference still in queued/processing voice-preview state
 * to failed. Used to clear the backlog when Modal timed out without firing
 * its FAILED webhook. The read path also self-heals after `olderThanMinutes`,
 * so most operators won't need this — it's the manual override.
 */
export async function resetStuckVoicePreviews(
  olderThanMinutes = 0,
): Promise<{ reset: number; scanned: number; total: number }> {
  return request("/admin/voice-previews/reset-stuck", {
    method: "POST",
    body: JSON.stringify({ olderThanMinutes }),
  });
}

// ── Update Reference Trim Defaults ────────────────────────────────────

export interface UpdateReferenceDefaultsResponse {
  success: boolean;
  name: string;
  defaultTrimStart: number | null;
  defaultTrimEnd: number | null;
  defaultClipS3Key: string | null;
  defaultClipDuration: number | null;
  defaultClipPreviewUrl?: string;
  transcript: string | null;
}

export async function updateReferenceDefaults(
  name: string,
  defaultTrimStart?: number | null,
  defaultTrimEnd?: number | null
): Promise<UpdateReferenceDefaultsResponse> {
  return request<UpdateReferenceDefaultsResponse>(
    `/references/${encodeRefName(name)}/defaults`,
    {
      method: "PUT",
      body: JSON.stringify({ defaultTrimStart, defaultTrimEnd }),
    }
  );
}
